/**
 * Renders the agent's reply as GitHub-Flavored Markdown (Contract C).
 *
 * ## Why this replaced a hand-rolled renderer
 *
 * The first version parsed a small subset by hand — paragraphs, bullets, bold,
 * inline code — because `react-markdown` was not yet a dependency. It had no
 * table support, so every table the agent produced rendered as raw pipes:
 *
 *     | Stage | Open Prospects |
 *     | :--- | :--- |
 *     | Inquiry | 6 |
 *
 * Tables are not a nice-to-have here. The instruction tells the agent to use one
 * for any answer with more than about three items, which is most useful answers,
 * so the most common reply shape was also the most unreadable.
 *
 * ## Security: the defaults are the defense
 *
 * `react-markdown` v10 does NOT render raw HTML and sanitizes link protocols
 * unless you opt out. **Do not add `rehype-raw` and do not override
 * `urlTransform`.** Replies legitimately quote prospect-authored free text —
 * activity subjects, notes, inbound messages — so enabling raw HTML would turn
 * "the agent quoted a note" into stored XSS. There is a test that feeds this a
 * script tag and asserts it renders as text.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export function AgentMarkdown({ text }: { text: string }) {
  if (!text) return null;

  return (
    <div className="agent-prose text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // A wide table must scroll INSIDE the bubble. Without its own
          // overflow container it widens the message, then the thread, and the
          // whole page ends up scrolling sideways.
          // Styled to match skySchedule, which gets this free from the Tailwind
          // Typography plugin (`prose prose-sm`). That plugin is not a dependency
          // here and adding one to the shared Tailwind config for a single panel
          // is not this feature's call — so the prose look is reproduced by hand.
          //
          // The first attempt drew a full bordered grid with a filled header,
          // which reads as a spreadsheet dropped into a conversation. Prose
          // tables are quieter: no outer box, no vertical rules, one line under
          // the header, and enough padding to breathe.
          // TABLE STYLING — third attempt, and the two failures are instructive.
          //
          // v1 was a full bordered grid with a filled header: a spreadsheet
          // dropped into a conversation. v2 stripped it to hairlines with no
          // container, which read as unformatted text.
          //
          // The actual defect in both was `w-full`. A three-column table
          // stretched across a 1100px panel puts a canyon between "Name" and
          // "Stage" and makes rows impossible to track by eye. Sizing to CONTENT
          // is what fixes the gaping — the rest is just giving it enough padding
          // and a light frame to read as a table.
          table: ({ children }) => (
            // `inline-block`, not `w-fit`. `w-fit` on a block element resolved
            // too narrow in this column and clipped the last cell of a row —
            // a table of long ids rendered with its count column cut off.
            // inline-block shrinks to the table's real width, `max-w-full` caps
            // it at the bubble, and overflow-x-auto scrolls the excess instead
            // of hiding it.
            <div className="my-3 inline-block max-w-full overflow-x-auto rounded-lg border border-border align-top">
              <table className="w-auto border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
          th: ({ children }) => (
            <th className="border-b border-border px-4 py-2.5 text-left font-semibold whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => (
            // tabular-nums so figures line up down a column; most cells are counts.
            <td className="border-b border-border/50 px-4 py-2.5 align-top tabular-nums">
              {children}
            </td>
          ),
          // Zebra striping: with rows this dense, alternating grounds are what
          // let the eye follow one row across three columns without a ruler.
          tbody: ({ children }) => (
            <tbody className="[&>tr:nth-child(even)]:bg-muted/20 [&>tr:last-child>td]:border-b-0">
              {children}
            </tbody>
          ),

          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0">{children}</ol>
          ),
          /*
            NO BOLD IN CHAT ANSWERS. Asked for repeatedly by the human, and this
            is the enforcement point.

            The model emphasises an inline label at the head of nearly every
            bullet — "Pricing Concerns:", "Discount Request:", "Sentiment:" — so
            an answer arrives as a column of bold fragments. Emphasis landing on
            most of the lines stops marking anything as important and just reads
            as shouting.

            Rendered as PLAIN TEXT rather than stripped from the source, and that
            distinction matters: the markdown still parses, so `**Sentiment:**`
            yields `Sentiment:` and literal asterisks can never reach the screen.
            A source-level strip gets that wrong the first time the model writes
            an unbalanced pair.

            Headings keep their weight — they are structure, not emphasis — and
            so do table headers. This is about `**…**` inside prose.
          */
          strong: ({ children }) => <>{children}</>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => (
            <code className="rounded bg-muted px-1 py-0.5 text-[0.85em]">{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="my-2 overflow-x-auto rounded-md bg-muted p-3 text-sm">{children}</pre>
          ),
          h1: ({ children }) => <h1 className="mt-3 mb-1 text-base font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="mt-3 mb-1 text-sm font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-semibold">{children}</h3>,
          blockquote: ({ children }) => (
            // `text-sm` and the padding resets are NOT redundant. The legacy global
            // sheet (`src/css/skypointcloud.css`) styles the bare `blockquote`
            // element with `font-size: 17.5px; padding: 10px 20px`, so a quote
            // rendered here came out 25% larger than the surrounding answer and
            // over-padded — reading as a deliberate callout when it is usually the
            // model drawing a pseudo-card it could not create for real. Every other
            // element in this map happens to carry its own size; this one did not,
            // so the global won. Element selectors lose to these class utilities.
            <blockquote className="my-2 border-l-2 border-border py-0 pr-0 pl-3 text-sm text-muted-foreground">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-3 border-border" />,
          // Any link the agent produces goes to another site as far as we know,
          // so it opens in a new tab and cannot reach back through window.opener.
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2"
            >
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export default AgentMarkdown;
